// AvatarStack — overlapping initial-bubbles for a set of people/agents, with a
// "+N" overflow chip. Extracted from ProjectsView's member avatars so the same
// treatment can be reused for the agents that participate in a workflow.
//
// Each item is resolved loosely so it works for org members ({display_name,
// user_id, email, ...}) and for agents ({name, id}) alike.

// Deterministic gradient per item so a bubble keeps a stable color across renders.
export const AVATAR_GRADIENTS = [
  'from-purple-500 to-pink-500',
  'from-fuchsia-500 to-purple-600',
  'from-violet-500 to-indigo-500',
  'from-pink-500 to-rose-500',
  'from-indigo-500 to-purple-500',
  'from-purple-600 to-fuchsia-500',
];

const keyOf = (it) => it.key || it.user_id || it.id || it.email || it.name || '';
const nameOf = (it) =>
  it.display_name || it.name || it.username || it.email || it.label || 'Member';
const initialOf = (it) => (nameOf(it).charAt(0) || '?').toUpperCase();

export const gradientForKey = (key) => {
  let hash = 0;
  for (let i = 0; i < String(key).length; i++) {
    hash = (hash * 31 + String(key).charCodeAt(i)) >>> 0;
  }
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const SIZES = {
  sm: { bubble: 'w-6 h-6 text-[10px]', gap: '-space-x-1.5', chipOffset: '-ml-1.5' },
  md: { bubble: 'w-7 h-7 text-[11px]', gap: '-space-x-2', chipOffset: '-ml-2' },
};

/**
 * @param {Object} props
 * @param {Array}  props.items - people/agents to render (resolved loosely)
 * @param {number} [props.max=4] - how many bubbles before collapsing to "+N"
 * @param {'sm'|'md'} [props.size='md']
 * @param {string} [props.ring='ring-[#0a0a0a]'] - ring color to blend with the surface
 */
export default function AvatarStack({ items = [], max = 4, size = 'md', ring = 'ring-[#0a0a0a]' }) {
  if (!items.length) return null;
  const s = SIZES[size] || SIZES.md;
  const shown = items.slice(0, max);
  const extra = items.length - shown.length;

  return (
    <div className="flex items-center">
      <div className={`flex ${s.gap}`}>
        {shown.map((it) => {
          const key = keyOf(it);
          return (
            <div
              key={key}
              title={nameOf(it)}
              className={`${s.bubble} rounded-full ring-2 ${ring} flex items-center justify-center font-semibold text-white bg-gradient-to-br ${gradientForKey(key)}`}
            >
              {initialOf(it)}
            </div>
          );
        })}
      </div>
      {extra > 0 && (
        <div
          className={`${s.bubble} ${s.chipOffset} rounded-full ring-2 ${ring} bg-white/10 flex items-center justify-center font-semibold text-white/80`}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}
